import * as React from 'react';
import { findIndex, uniqBy } from 'lodash';
import { Label } from '@atlaskit/field-base';
import { AtlaskitThemeProvider } from '@atlaskit/theme';
import { BaseBlockView } from '../base-block.view';
import styled from 'styled-components';
import { getCenterOfPageStyle } from '../../../util/style/get-center-of-page-style';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../../interface/component/injected-props.interface';
import { FormModel } from '../../../model/block';
import { findLinkedData } from '../../../util/page/find-linked-data.util';
import { IFieldConfig } from '../../../interface/field-config.interface';
import { FieldsEnum } from '../../../enum/fields.enum';
import { InputField } from './fields/input-field';
import { DateField } from './fields/date-field';
import { EnumField } from './fields/enum-field';
import { isFormData } from '../../../util/linked-data/is-form-data.util';
import { CheckableField } from './fields/checkable-field';
import { action, computed, observable, runInAction } from 'mobx';
import { FormLinkedDataModel, IFormData } from '../../../model/linked-data';
import { submitForm } from '../../../util/action/type/submit-form-action.util';
import { draftToString } from '../../../util/draft/draft-to-string.util';
import { isValidEmail } from '../../../util/string/is-valid-email.util';
import { isValidPhone } from '../../../util/string/is-valid-phone.util';
import { isStringEmpty } from '../../../util/string/is-string-empty.util';
import { ColorEnum } from '../../../enum/color.enum';
import { LinkedinLoginButton } from '../../linkedin-login-button/linkedin-login-button';
import { ILinkedInLoginResponse } from '../../../interface/component/linkedin-login-response.interface';
import { LinkedInCard } from '../../linked-in-card/linked-in-card';
import { SubmittedForm } from './submitted-form.view';
import { Ellipsis } from '../../ellipsis';
import Translate from '../../translate/translate';
import { LARGE_SCREEN_SIZE } from '../../../constants';
import { BlocksDialog, keylineHeight } from '../blocks-dialog';
import { CmsButton } from '../../cms-button/cms-button';
import { ErrorWrapper, InvalidWrapper } from '../../atoms';
import { LinkedInRefEnum } from '../../../enum/linked-in-ref.enum';
import { SliderField } from './fields/slider-field';
import { GradingField } from './fields/grading-field';
import { BoolField } from './fields/bool-field';
import { LinkedinLoginTypeEnum } from '../../../enum/linkedin-login-type.enum';
import { getTranslation } from '../../../util/translation/get-translation';
import { FormErrorIcon } from '../../icons';
import { BlockColorEnum } from '../../../enum/block-color.enum';
import { addOpacity } from '../../../util/style/add-opacity.util';
import { largeMobileMedia } from '../../../util/style/large-mobile-media';
import { GoogleTagManager } from '../../../google-tag-manager';
import { LinkedDataErrorCodes } from '../../../interface/linked-data-error.interface';
import { FileField, IFileData } from './fields/file-field';
import { CmsApiErrorCodes } from '../../../enum/cms-api-error-codes.enum';
import { AjaxError } from 'rxjs';
import { Logger } from '../../../util/debug/logger.util';
import { PhoneField } from './fields/phone-field';
import { decodeQuery } from '../../../util/url/decode-query.util';

const FORM_ID = 'form';

interface IGroupWrapperProps {
  horizontal: boolean;
  labelColor: string;
}

function isFileField(fieldConfig: IFieldConfig): boolean {
  return (
    fieldConfig.type === FieldsEnum.SINGLE_FILE_INPUT ||
    fieldConfig.type === FieldsEnum.MULTI_FILE_INPUT
  );
}

// @ts-ignore TODO2024 Investigate the TS error `Type of property 'defaultProps' circularly reference...`
const GroupWrapper = styled.div`
  position: relative;
  width: 100%;
  justify-content: space-between;
  flex-wrap: wrap;
  display: ${(props: IGroupWrapperProps) => (props.horizontal ? 'flex' : 'block')};
  & label div {
    color: ${(props: IGroupWrapperProps) => props.labelColor};
    font-weight: bold;
    font-size: 16px;
  }
  ${largeMobileMedia(`
    display: block;
  `)}
`;

const FormInvalidWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(props) => addOpacity(BlockColorEnum.ErrorColor(props), 0.85)};
  padding: 14px 12px;
  border-radius: 3px;
`;

const FormInvalidRowWrapper = styled.div`
  display: flex;
  color: ${BlockColorEnum.SecondaryTextColor};
  align-items: center;
  font-size: 14px;
`;

const FooterWrapper = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  transition: box-shadow 200ms;
  box-shadow: ${`0 -${keylineHeight}px 0 0 ${ColorEnum.AltoGray}`};
`;

const SampleFormWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 20px 13px;
  ${getCenterOfPageStyle()}
`;

const FormViewWrapper = styled.form`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  flex-direction: column;
`;
const CenterViewWrapper = styled.div`
  padding: 20px;
  margin: 0 auto;
  ${getCenterOfPageStyle()}
`;
const FormFieldContainer = styled.div`
  flex: 0 0 auto;
  justify-content: center;
  & label div {
    font-weight: normal;
  }
  ${(props: { horizontal: boolean }) => (props.horizontal ? 'width: calc(50% - 5px);' : '')}
  ${largeMobileMedia(`
    width: 100%;
  `)}
`;

const SubmitContainer = styled.div`
  margin-top: 15px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface IValidatorMessage {
  validator: (str: string) => boolean;
  invalidityMessage: string;
}

const LinkedinLoginButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  flex-direction: column;
  max-width: 100%;
`;

const OrContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  align-self: stretch;
  color: ${ColorEnum.ContentColor};
  & hr {
    flex: 1;
    border: 1px solid ${ColorEnum.ContentColor};
  }
`;

const ConsentContainer = styled.div`
  margin-top: 28px;
`;

const PopupContentContainer = styled.div`
  padding: 10px;
`;

const PleaseWaitHolder = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  padding: 10px 5px 20px;
  background: ${BlockColorEnum.TertiaryBackgroundColor};
  color: ${BlockColorEnum.SecondaryTextColor};
  text-align: center;
  z-index: 999999999;
  box-shadow: -5px 0 5px -5px #333;
`;

@inject('state', 'history', 'dispatch')
@observer
export class FormView extends BaseBlockView<FormModel, IInjectedProps> {
  @observable.shallow formData: {
    fields: { [key: string]: any };
    files: { [key: string]: IFileData };
  } = { fields: {}, files: {} };
  @observable fieldValidationResult: { [key: string]: { valid: boolean; message: string } } = {};
  @observable queryData: { [key: string]: any } = {};
  @observable isSubmitted = false;
  @observable fetchedLinkedInData = false;
  @observable linkedInLoginResponse: ILinkedInLoginResponse;
  @observable popupContent: string = '';
  @observable modalWidth: number;
  @observable submitted: string;
  @observable uploading: { [key: string]: boolean } = {};
  @observable consentScrolledToBottom: object = {};
  @observable activeConsent: any;
  @observable isConfirmed: boolean = false;
  submitTimeout;

  onFileError = (fieldKey: string) => (errorCode?: CmsApiErrorCodes) => {
    this.formData.files[fieldKey] = undefined;
    this.uploading[fieldKey] = undefined;
    if (!errorCode) {
      const unknownFileErrorTile = draftToString(
        this.props.content.unknownFileErrorTitle,
        this.props.state,
      );
      const unknownFileErrorBody = draftToString(
        this.props.content.unknownFileErrorBody,
        this.props.state,
      );
      return this.props.dispatch.addGlobalError(unknownFileErrorTile, unknownFileErrorBody);
    }
    if (errorCode === CmsApiErrorCodes.InvalidFileFormat) {
      const invalidFileFormatTitle = draftToString(
        this.props.content.invalidFileFormatTitle,
        this.props.state,
      );
      const invalidFileFormatBody = draftToString(
        this.props.content.invalidFileFormatBody,
        this.props.state,
      );
      return this.props.dispatch.addGlobalError(invalidFileFormatTitle, invalidFileFormatBody);
    }
    if (errorCode === CmsApiErrorCodes.EmptyFile) {
      const emptyFileTitle = draftToString(
        this.props.content.emptyFileUploadedErrorTitle,
        this.props.state,
      );
      const emptyFileBody = draftToString(
        this.props.content.emptyFileUploadedErrorBody,
        this.props.state,
      );
      return this.props.dispatch.addGlobalError(emptyFileTitle, emptyFileBody);
    }
    if (errorCode === CmsApiErrorCodes.FileLimitExceeded) {
      const fileSizeExceededTitle = draftToString(
        this.props.content.fileSizeExceededErrorTitle,
        this.props.state,
      );
      const fileSizeExceededBody = draftToString(
        this.props.content.fileSizeExceededErrorBody,
        this.props.state,
      );
      return this.props.dispatch.addGlobalError(fileSizeExceededTitle, fileSizeExceededBody);
    }
    if (errorCode === CmsApiErrorCodes.FileNameInvalid) {
      const fileNameInvalidTitle = draftToString(
        this.props.content.fileNameInvalidTitle,
        this.props.state,
      );
      const fileNameInvalidBody = draftToString(
        this.props.content.fileNameInvalidBody,
        this.props.state,
      );
      return this.props.dispatch.addGlobalError(fileNameInvalidTitle, fileNameInvalidBody);
    }
    const serverErrorTitle = draftToString(this.props.content.serverErrorTitle, this.props.state);
    const serverErrorBody = draftToString(this.props.content.serverErrorBody, this.props.state);
    this.props.dispatch.addGlobalError('', `${serverErrorTitle}\n${serverErrorBody}`);
  };

  renderComponent = (
    index: number,
    hasSiblings: boolean,
    fieldConfig: IFieldConfig,
    onChange,
    onBlur,
  ) => {
    let value;
    if (isFileField(fieldConfig)) {
      value = this.formData.files[fieldConfig.applyFormKey];
    } else {
      value = this.formData.fields[fieldConfig.applyFormKey];
    }
    if (fieldConfig.type === FieldsEnum.BOOL && value === undefined && hasSiblings) {
      this.formData.fields[fieldConfig.applyFormKey] = false;
      value = false;
    }
    const isValid =
      !this.fieldValidationResult[fieldConfig.applyFormKey] ||
      this.fieldValidationResult[fieldConfig.applyFormKey].valid;
    const invalidityMessage =
      this.fieldValidationResult[fieldConfig.applyFormKey] &&
      this.fieldValidationResult[fieldConfig.applyFormKey].message;

    const placeholder =
      this.props.content.fieldTranslations[fieldConfig.placeholder] || fieldConfig.placeholder;
    const label = this.props.content.fieldTranslations[fieldConfig.label] || fieldConfig.label;
    const labelColor = this.props.state.colors.getColor(this.props.content.inputLabelColor);
    switch (fieldConfig.type) {
      case FieldsEnum.PHONE:
        return (
          <PhoneField
            key={fieldConfig.applyFormKey}
            {...fieldConfig}
            disabled={this.isWaiting}
            label={label}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            showAllCountriesLabel={draftToString(
              this.props.content.showAllCountriesLabel,
              this.props.state,
            )}
            hideAllCountriesLabel={draftToString(
              this.props.content.hideAllCountriesLabel,
              this.props.state,
            )}
            closeCountryPickerLabel={draftToString(
              this.props.content.closeCountryPickerLabel,
              this.props.state,
            )}
            preferredCountries={this.props.content.preferredCountries}
            isValid={isValid}
            invalidityMessage={invalidityMessage}
            labelColor={labelColor}
            defaultCountryOnNew={this.props.content.defaultCountry}
            searchIconColor={this.props.content.searchIconColor}
            buttonColor={this.props.content.submitButtonBackgroundColor}
          />
        );
      case FieldsEnum.INPUT:
      case FieldsEnum.URL:
      case FieldsEnum.SSN:
      case FieldsEnum.ZIP:
      case FieldsEnum.EMAIL:
      case FieldsEnum.MULTILINE_INPUT:
        return (
          <InputField
            key={fieldConfig.applyFormKey}
            {...fieldConfig}
            disabled={this.isWaiting}
            label={label}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            multiline={fieldConfig.type === FieldsEnum.MULTILINE_INPUT}
            isValid={isValid}
            invalidityMessage={invalidityMessage}
            labelColor={labelColor}
          />
        );
      case FieldsEnum.DATE:
        return (
          <DateField
            key={fieldConfig.applyFormKey}
            {...fieldConfig}
            disabled={this.isWaiting}
            placeholder={placeholder}
            label={label}
            isInvalid={!isValid}
            onBlur={onBlur}
            onChange={onChange}
          />
        );
      case FieldsEnum.SINGLE_FILE_INPUT:
      case FieldsEnum.MULTI_FILE_INPUT:
        return (
          <FileField
            key={fieldConfig.applyFormKey}
            uploadFileErrorMessagePrefix={draftToString(
              this.props.content.uploadFileErrorMessage,
              this.props.state,
            )}
            availableTypes={this.props.content.availableFileTypes}
            fileSizeLimit={this.props.content.fileSizeLimit * 1024 * 1024}
            labelColor={labelColor}
            maxFilesLimit={fieldConfig.maxFilesLimit}
            onBlur={onBlur}
            disabled={this.isWaiting}
            required={fieldConfig.required}
            onUploadStarted={action(
              () =>
                (this.uploading = Object.assign({}, this.uploading, {
                  [fieldConfig.applyFormKey]: true,
                })),
            )}
            onUploadFinished={action(() => {
              this.uploading = Object.assign({}, this.uploading, {
                [fieldConfig.applyFormKey]: false,
              });
              if (!this.isUploading && Boolean(this.submitted)) {
                if (this.submitTimeout) {
                  clearTimeout(this.submitTimeout);
                }
                this.submitTimeout = setTimeout(
                  action(() => {
                    this.submitTimeout = undefined;
                    const url = this.submitted;
                    this.submitted = undefined;
                    if (this.isFormValid.valid) {
                      this.submitForm(url);
                      this.submitTimeout = undefined;
                    }
                  }),
                  100,
                );
              }
            })}
            dropFileMessage={draftToString(this.props.content.dropFileMessage, this.props.state)}
            noFileChosenMessage={draftToString(
              this.props.content.noFileChosenMessage,
              this.props.state,
            )}
            uploadMoreDocumentsMessage={draftToString(
              this.props.content.uploadMoreDocumentsMessage,
              this.props.state,
            )}
            replaceFileMessage={draftToString(
              this.props.content.replaceFileMessage,
              this.props.state,
            )}
            onChange={(v) => {
              onChange(v);
              onBlur();
            }}
            onError={(error: AjaxError) => {
              this.onFileError(fieldConfig.applyFormKey)(
                error.response && error.response.errorCode,
              );
              Logger.warn(error.name, error.message);
            }}
            onFileSizeError={() =>
              this.onFileError(fieldConfig.applyFormKey)(CmsApiErrorCodes.FileLimitExceeded)
            }
            onFileNameError={() =>
              this.onFileError(fieldConfig.applyFormKey)(CmsApiErrorCodes.FileNameInvalid)
            }
            isInvalid={!isValid}
            name={fieldConfig.applyFormKey}
            placeholder={placeholder}
            label={label}
            files={value || []}
            multiple={fieldConfig.type === FieldsEnum.MULTI_FILE_INPUT}
          />
        );
      case FieldsEnum.ENUM:
        return (
          <EnumField
            key={fieldConfig.applyFormKey}
            {...fieldConfig}
            disabled={this.isWaiting}
            isInvalid={!isValid}
            placeholder={placeholder}
            label={label}
            onBlur={onBlur}
            labelColor={labelColor}
            onChange={onChange}
            selected={value}
          />
        );
      case FieldsEnum.SLIDER:
        return (
          <SliderField
            key={fieldConfig.applyFormKey}
            {...fieldConfig}
            disabled={this.isWaiting}
            placeholder={placeholder}
            label={label}
            isInvalid={!isValid}
            onBlur={onBlur}
            labelColor={labelColor}
            onChange={onChange}
            value={value}
          />
        );
      case FieldsEnum.STARS:
        return (
          <GradingField
            key={fieldConfig.applyFormKey}
            {...fieldConfig}
            disabled={this.isWaiting}
            placeholder={placeholder}
            label={label}
            onBlur={onBlur}
            isInvalid={!isValid}
            labelColor={labelColor}
            onChange={onChange}
            value={value}
          />
        );
      case FieldsEnum.BOOL:
        return (
          <BoolField
            disabled={this.isWaiting}
            label={fieldConfig.label}
            yesTranslation={this.props.content.yesTranslation}
            noTranslation={this.props.content.noTranslation}
            value={value}
            onBlur={onBlur}
            name={fieldConfig.applyFormKey}
            onChange={onChange}
            hasSiblings={hasSiblings}
            isInvalid={!isValid}
            required={fieldConfig.required}
          />
        );
    }
    return (
      <InvalidWrapper key={fieldConfig.applyFormKey}>
        Config not defined for {fieldConfig.type}
      </InvalidWrapper>
    );
  };

  componentDidMount(): void {
    if (!this.props.inEditor && !this.formLinkedData.data.jobAdIsPublished) {
      this.props.dispatch.addGlobalError(
        this.props.state.adminSettings.errorMessageJobAdNotPublishedText,
        '',
      );
    }
    if (!this.props.inEditor && this.formLinkedData.data.jobAdIsExpired) {
      this.props.dispatch.addGlobalError(
        this.props.state.adminSettings.errorMessageJobAdExpiredText,
        '',
      );
    }
  }

  componentWillUnmount(): void {
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
      this.submitTimeout = undefined;
    }
  }

  submitForm = (url: string) => {
    if (this.isUploading) {
      runInAction(() => {
        this.submitted = url;
      });
      return;
    }

    // Added to support query parameters in the form submit. Required for Keeparo UTM tags.
    const urlParams: { [key: string]: string } = this.props.history.location.search
      ? decodeQuery(this.props.history.location.search)
      : {};

    this.props
      .dispatch(
        submitForm(
          url,
          this.formData,
          {
            serverErrorTitle: draftToString(this.props.content.serverErrorTitle, this.props.state),
            serverErrorBody: draftToString(this.props.content.serverErrorBody, this.props.state),
            alreadyAppliedErrorTitle: draftToString(
              this.props.content.alreadyAppliedErrorTitle,
              this.props.state,
            ),
            alreadyAppliedErrorBody: draftToString(
              this.props.content.alreadyAppliedErrorBody,
              this.props.state,
            ),
            invalidFileFormatTitle: draftToString(
              this.props.content.invalidFileFormatTitle,
              this.props.state,
            ),
            invalidFileFormatBody: draftToString(
              this.props.content.invalidFileFormatBody,
              this.props.state,
            ),
            emptyFileUploadedErrorMessageTitle: draftToString(
              this.props.content.emptyFileUploadedErrorTitle,
              this.props.state,
            ),
            emptyFileUploadedErrorMessageBody: draftToString(
              this.props.content.emptyFileUploadedErrorBody,
              this.props.state,
            ),
          },
          this.queryData,
          urlParams,
        ),
      )
      .then(
        action((returnValue) => {
          if (returnValue === true) {
            if (this.props.state.adminSettings.tagManagerTrackCustomEvents) {
              GoogleTagManager.pushCustomFormSubmitEvent(this.props.state, this.formLinkedData);
            } else {
              GoogleTagManager.push({
                event: 'form_submitted',
                form_data: this.formLinkedData.displayName,
                form_data_id: this.formLinkedData.mentionId,
              });
            }
            this.isSubmitted = true;
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
        }),
      );
  };

  @action
  onChangeFormData = (key, value) => {
    const field = this.formLinkedData.data.fields.find((f) => f.applyFormKey === key);
    if (isFileField(field)) {
      this.formData.files = Object.assign({}, this.formData.files, {
        [key]: value,
      });
    } else {
      this.formData.fields = Object.assign({}, this.formData.fields, {
        [key]: value,
      });
    }
  };

  @action
  onChangeFormDataMultiple = (values: { [key: string]: string }) => {
    this.formData = Object.assign({}, this.formData, values);
  };

  @action
  onChangeQueryData = (key, value) => {
    if (
      key === 'consentId' &&
      this.props.content.forceOpenConsent &&
      !this.consentScrolledToBottom[this.activeConsent]
    ) {
      return;
    }
    this.queryData = Object.assign({}, this.queryData, {
      [key]: value,
    });
  };

  @action
  onLinkedInLogin = (profile: ILinkedInLoginResponse) => {
    const formData = this.formLinkedData.data;
    const firstNameField = formData.fields.find(
      (field) => field.linkedInRef === LinkedInRefEnum.FIRST_NAME,
    );
    const surNameField = formData.fields.find(
      (field) => field.linkedInRef === LinkedInRefEnum.SUR_NAME,
    );
    const emailField = formData.fields.find((field) => field.linkedInRef === LinkedInRefEnum.EMAIL);
    const linkedInField = formData.fields.find(
      (field) => field.linkedInRef === LinkedInRefEnum.LINKED_IN_URL,
    );
    if (firstNameField) {
      this.onChangeFormData(firstNameField.applyFormKey, profile.firstName);
    }
    if (surNameField) {
      this.onChangeFormData(surNameField.applyFormKey, profile.lastName);
    }
    if (emailField) {
      this.onChangeFormData(emailField.applyFormKey, profile.emailAddress);
    }
    if (linkedInField) {
      this.onChangeFormData(linkedInField.applyFormKey, profile.publicProfileUrl);
    }
    this.fetchedLinkedInData = true;
    this.linkedInLoginResponse = profile;
  };

  @action closePopup = (ev) => {
    if (ev.target.protocol === 'mailto:') {
      // mailto: links do propagate the event but we don't want to close the popup when user clicks on a mailto: link
      return;
    }
    this.popupContent = null;
    if (this.props.content.forceOpenConsent && this.isConfirmed && this.activeConsent) {
      this.onChangeQueryData('consentId', this.activeConsent);
      const consentValidation = this.validateConsent();
      this.fieldValidationResult = Object.assign({}, this.fieldValidationResult, {
        consentId: consentValidation,
      });
    }
    this.isConfirmed = false;
  };

  @computed
  get emptyStringMessage() {
    return draftToString(this.props.content.emptyStringMessage, this.props.state);
  }

  @computed
  get validators(): {
    [key in FieldsEnum]?: IValidatorMessage;
  } {
    return {
      [FieldsEnum.EMAIL]: {
        validator: isValidEmail,
        invalidityMessage: draftToString(this.props.content.invalidEmailMessage, this.props.state),
      },
      [FieldsEnum.PHONE]: {
        validator: isValidPhone,
        invalidityMessage: draftToString(
          this.props.content.invalidPhoneNumberMessage,
          this.props.state,
        ),
      },
    };
  }

  @computed
  get configuration() {
    return this.props.state.configuration;
  }

  validateFieldConfig = (fieldConfig: IFieldConfig) => {
    let value;
    if (isFileField(fieldConfig)) {
      value = this.formData.files[fieldConfig.applyFormKey];
      if (
        (!value || value.length === 0 || (value.length > 0 && value[0] === undefined)) &&
        fieldConfig.required
      ) {
        return {
          valid: false,
          message: draftToString(this.props.content.requiredFieldMessage, this.props.state),
        };
      }
    } else {
      value = this.formData.fields[fieldConfig.applyFormKey];
    }
    if (value === undefined && fieldConfig.required) {
      return {
        valid: false,
        message: draftToString(this.props.content.requiredFieldMessage, this.props.state),
      };
    }
    if (fieldConfig.type === FieldsEnum.MULTILINE_INPUT || fieldConfig.type === FieldsEnum.INPUT) {
      if (value && fieldConfig.maxChars && value.length > fieldConfig.maxChars) {
        return {
          valid: false,
          message: draftToString(this.props.content.exceededMaxCharsMessage, this.props.state),
        };
      }
    }
    if (typeof value === 'string') {
      if (isStringEmpty(value) && fieldConfig.required) {
        return {
          valid: false,
          message: draftToString(this.props.content.requiredFieldMessage, this.props.state),
        };
      }

      const validators = this.validators;
      if (
        !isStringEmpty(value) &&
        validators[fieldConfig.type] &&
        !validators[fieldConfig.type].validator(value)
      ) {
        return {
          valid: false,
          message: validators[fieldConfig.type].invalidityMessage,
        };
      }
    }
    return {
      valid: true,
      message: '',
    };
  };

  validateConsent = () => {
    if (Object.keys(this.queryData).length === 0) {
      return {
        valid: false,
        message: draftToString(this.props.content.consentMissingMessage, this.props.state),
      };
    }
    return {
      valid: true,
      message: '',
    };
  };

  @computed
  get isFormValid(): { valid: boolean; message: string } {
    if (this.props.inEditor) {
      return {
        valid: false,
        message: getTranslation(this.props.state.configuration, 'form.in-editor'),
      };
    }

    const selectedMention = this.props.content.selectedMention;
    const foundData = findLinkedData(selectedMention, this.props.state.activeDesign.data);
    if (isFormData(foundData)) {
      const formData = foundData.data;
      const fields = (formData.fields || []) as IFieldConfig[];
      for (const fieldConfig of fields) {
        const fieldValidation = this.validateFieldConfig(fieldConfig);
        if (!fieldValidation.valid) {
          return fieldValidation;
        }
      }
    }
    const consentValidation = this.validateConsent();
    if (!consentValidation.valid) {
      return consentValidation;
    }
    return {
      valid: true,
      message: '',
    };
  }

  @action
  validateWholeForm = () => {
    const selectedMention = this.props.content.selectedMention;
    const foundData = findLinkedData(selectedMention, this.props.state.activeDesign.data);
    if (isFormData(foundData)) {
      const formData = foundData.data;
      const fields = (formData.fields || []) as IFieldConfig[];
      for (const fieldConfig of fields) {
        const fieldValidation = this.validateFieldConfig(fieldConfig);
        this.fieldValidationResult = Object.assign({}, this.fieldValidationResult, {
          [fieldConfig.applyFormKey]: fieldValidation,
        });
      }
    }

    const consentValidation = this.validateConsent();
    this.fieldValidationResult = Object.assign({}, this.fieldValidationResult, {
      consentId: consentValidation,
    });
  };

  @action
  onBottomContentVisibilityChange = (isVisible: boolean) => {
    if (isVisible) {
      this.consentScrolledToBottom = {
        ...this.consentScrolledToBottom,
        [this.activeConsent]: true,
      };
    }
  };

  @computed
  get applyWithLinkedIn() {
    if (
      this.props.state.adminSettings.linkedInLoginType === LinkedinLoginTypeEnum.None ||
      !this.props.content.linkedInEnabled
    ) {
      return null;
    }

    if (isStringEmpty(this.props.state.adminSettings.linkedInClientId)) {
      return (
        <InvalidWrapper>
          <Ellipsis>
            {draftToString(this.props.content.missingLinkedInClientId, this.props.state)}
          </Ellipsis>
        </InvalidWrapper>
      );
    }

    if (this.fetchedLinkedInData) {
      return (
        <LinkedInCard
          currentLastPositionText={draftToString(
            this.props.content.currentLastPositionText,
            this.props.state,
          )}
          fillOtherInfoText={draftToString(this.props.content.fillOtherInfoText, this.props.state)}
          firstName={this.linkedInLoginResponse.firstName}
          lastName={this.linkedInLoginResponse.lastName}
          pictureUrl={this.linkedInLoginResponse.pictureUrl}
          publicProfileUrl={this.linkedInLoginResponse.publicProfileUrl}
          headline={this.linkedInLoginResponse.headline}
        />
      );
    }

    return (
      <LinkedinLoginButtonContainer>
        <LinkedinLoginButton
          disableAPI={this.props.inEditor}
          clientId={this.props.state.adminSettings.linkedInClientId}
          onLogin={this.onLinkedInLogin}
          loginText={
            <Ellipsis>
              {draftToString(this.props.content.linkedInLoginTextMessage, this.props.state)}
            </Ellipsis>
          }
        />
        <OrContainer>
          <hr />
          <Ellipsis
            style={{
              textTransform: 'uppercase',
              paddingLeft: 7,
              paddingRight: 7,
            }}
          >
            {draftToString(this.props.content.orMessage, this.props.state)}
          </Ellipsis>
          <hr />
        </OrContainer>
      </LinkedinLoginButtonContainer>
    );
  }

  getConsentForm(formData: IFormData) {
    const consent = formData.consent;
    if (!consent) {
      return null;
    }
    const consentFullShortText = draftToString(
      this.props.content.consentFullShortText,
      this.props.state,
    );
    const consentLimitedShortText = draftToString(
      this.props.content.consentLimitedShortText,
      this.props.state,
    );
    let type: 'checkbox' | 'radio' = 'checkbox';
    const items = [{ value: consent.id, label: consentFullShortText }];
    if (!isStringEmpty(consentLimitedShortText)) {
      type = 'radio';
      items.push({ value: consent.limitedId, label: consentLimitedShortText });
    }
    const labelColor = this.props.state.colors.getColor(this.props.content.groupLabelColor);
    return (
      <ConsentContainer>
        <CheckableField
          disabled={this.isWaiting}
          isInvalid={
            this.fieldValidationResult.consentId && !this.fieldValidationResult.consentId.valid
          }
          labelColor={labelColor}
          label={draftToString(this.props.content.consentHeaderText, this.props.state)}
          value={(this.queryData as any).consentId}
          name="consent"
          type={type}
          items={items}
          onTextClicked={action((value) => {
            this.popupContent = value === consent.id ? consent.text : consent.limitedText;
            this.modalWidth = document.getElementById(FORM_ID).getBoundingClientRect().width;
            this.activeConsent = value;
          })}
          onBlur={action(() => {
            const consentValidation = this.validateConsent();
            this.fieldValidationResult = Object.assign({}, this.fieldValidationResult, {
              consentId: consentValidation,
            });
          })}
          onChange={action((value: any, clickedField: any) => {
            if (this.props.content.forceOpenConsent && value !== undefined) {
              // if force consent selectin is done via Modal Confirm Button
              this.popupContent = clickedField === consent.id ? consent.text : consent.limitedText;
              this.modalWidth = document.getElementById(FORM_ID).getBoundingClientRect().width;
              this.activeConsent = value;
            } else {
              this.onChangeQueryData('consentId', value);
            }
          })}
        />
      </ConsentContainer>
    );
  }

  @computed
  get invalidMessages() {
    const keys = Object.keys(this.fieldValidationResult);
    const messages = [];
    for (const key of keys) {
      if (!this.fieldValidationResult[key].valid) {
        messages.push(this.fieldValidationResult[key]);
      }
    }
    return uniqBy(messages, 'message');
  }

  @computed
  get isFormInvalid() {
    if (this.props.inEditor) {
      return false;
    }
    return this.invalidMessages.length > 0;
  }

  @computed
  get isUploading() {
    return Object.keys(this.uploading).reduce(
      (isUploading, currentKey) => this.uploading[currentKey] || isUploading,
      false,
    );
  }

  @computed
  get isWaiting() {
    return Boolean(this.submitted) && this.isUploading;
  }

  getFormView(formData: IFormData) {
    if (this.formData) {
      const fields = (formData.fields || []) as IFieldConfig[];
      const submitButtonDisabled =
        formData.submitEndpoint === '' ||
        !this.isFormValid.valid ||
        this.isWaiting ||
        formData.jobAdIsExpired ||
        !formData.jobAdIsPublished;
      const submitButtonColor = submitButtonDisabled
        ? this.props.content.submitButtonBackgroundColorDisabled
        : this.props.content.submitButtonBackgroundColor;
      const fieldGroups: Array<{ elements: IFieldConfig[]; groupId: string; groupName: string }> =
        [];
      for (const field of fields) {
        let groupId = field.groupId;
        if (groupId === undefined) {
          groupId = field.label;
        }
        let fieldGroupIndex = findIndex(fieldGroups, { groupId: groupId });
        if (fieldGroupIndex === -1) {
          fieldGroupIndex = fieldGroups.length;
          fieldGroups.push({
            groupId: groupId,
            groupName: field.groupName,
            elements: [],
          });
        }
        fieldGroups[fieldGroupIndex].elements.push(field);
      }
      let skippedGroupCount = -1;
      return (
        <AtlaskitThemeProvider>
          <CenterViewWrapper>
            <FormViewWrapper id={FORM_ID}>
              {this.isWaiting ? (
                <PleaseWaitHolder>
                  {draftToString(this.props.content.fileUploadingMessage, this.props.state)}
                </PleaseWaitHolder>
              ) : undefined}
              {this.invalidMessages.length > 0 && (
                <FormInvalidWrapper>
                  {this.invalidMessages.map((message) => (
                    <FormInvalidRowWrapper key={message.message}>
                      <FormErrorIcon style={{ opacity: 0.7, marginRight: 10 }} />
                      {message.message}
                    </FormInvalidRowWrapper>
                  ))}
                </FormInvalidWrapper>
              )}
              {this.applyWithLinkedIn}
              {fieldGroups.map((group, index) => {
                if (!group.groupName) {
                  skippedGroupCount++;
                }
                return (
                  <GroupWrapper
                    horizontal={Number(group.groupId) === -1}
                    labelColor={
                      this.props.content.groupLabelColor
                        ? this.props.state.colors.getColor(this.props.content.groupLabelColor)
                        : this.props.state.blockColors.PrimaryButtonColor
                    }
                    key={group.groupId}
                  >
                    {group.groupName ? (
                      <Label
                        isRequired={false}
                        label={`${index - skippedGroupCount}. ${group.groupName}`}
                      />
                    ) : group.groupId === null ||
                      group.groupId === undefined ||
                      Number(group.groupId) > 0 ? (
                      <OrContainer>
                        <hr />
                      </OrContainer>
                    ) : undefined}
                    {group.elements.map((fieldConfig) => {
                      return (
                        <FormFieldContainer
                          horizontal={Number(group.groupId) === -1}
                          key={fieldConfig.applyFormKey}
                        >
                          {this.renderComponent(
                            index,
                            group.elements.length > 1,
                            fieldConfig,
                            (value: any) => {
                              this.onChangeFormData(fieldConfig.applyFormKey, value);
                            },
                            action(() => {
                              const fieldValidation = this.validateFieldConfig(fieldConfig);
                              this.fieldValidationResult = Object.assign(
                                {},
                                this.fieldValidationResult,
                                { [fieldConfig.applyFormKey]: fieldValidation },
                              );
                            }),
                          )}
                        </FormFieldContainer>
                      );
                    })}
                  </GroupWrapper>
                );
              })}
              {this.getConsentForm(formData)}
              <SubmitContainer>
                <CmsButton
                  disabled={submitButtonDisabled}
                  color={submitButtonColor}
                  onClick={() => this.submitForm(formData.submitEndpoint)}
                  onDisabledClick={this.validateWholeForm}
                  text={draftToString(this.props.content.submitButtonText, this.props.state)}
                />
              </SubmitContainer>
            </FormViewWrapper>
          </CenterViewWrapper>
        </AtlaskitThemeProvider>
      );
    }

    return <FormViewWrapper>No fields</FormViewWrapper>;
  }

  @computed
  get formLinkedData(): FormLinkedDataModel {
    const selectedMention = this.props.content.selectedMention;
    const foundData = findLinkedData(selectedMention, this.props.state.activeDesign.data);
    if (!isFormData(foundData)) {
      return null;
    }
    return foundData;
  }

  render() {
    if (!this.formLinkedData) {
      return null;
    }
    if (isStringEmpty(draftToString(this.props.content.consentFullShortText, this.props.state))) {
      return <InvalidWrapper>Provide full consent short text</InvalidWrapper>;
    }
    if (this.isSubmitted) {
      return (
        <SubmittedForm
          blockColor={this.props.state.colors.getColor(this.props.content.successAppliedBlockColor)}
          boxColor={this.props.state.colors.getColor(this.props.content.successAppliedBoxColor)}
          successHeader={draftToString(this.props.content.successMessageHeader, this.props.state)}
          successBody={draftToString(this.props.content.successMessageBody, this.props.state)}
        />
      );
    }
    if (this.props.inEditor) {
      console.log(this.props.content.fieldTranslations, this.formLinkedData.dataType);
      const initialFieldsData = this.formLinkedData.dataType.map((fd) => {
        const [label, placeholder] = fd.displayName.split(':');
        const fieldtype = fd.type as any;
        return {
          label: this.props.content.fieldTranslations[label] || label,
          type: fieldtype,
          applyFormKey: 'text',
          maxChars: 50,
          placeholder: this.props.content.fieldTranslations[placeholder] || placeholder,
          required: false,
        };
      });
      const initialFormData = {
        submitEndpoint: '',
        submitButtonText: '',
        jobAdIsExpired: false,
        jobAdIsPublished: true,
        fields: initialFieldsData,
        consent: {
          id: '1',
          limitedId: '2',
          text: 'Example Consent',
          limitedText: 'Example Limited Consent',
        },
      };
      return (
        <SampleFormWrapper>
          {!this.props.content.selectedMention ? (
            <Translate>form.edit.placeholder</Translate>
          ) : (
            this.getFormView(initialFormData)
          )}
        </SampleFormWrapper>
      );
    }

    const modalActions = [
      {
        text: draftToString(this.props.content.consentConfirmText, this.props.state),
        onClick: this.closePopup,
        appearance: 'primary',
      },
    ];
    return (
      <>
        {this.popupContent && (
          <BlocksDialog
            onBottomContentVisibilityChange={this.onBottomContentVisibilityChange}
            containerStyle={{ padding: 20 }}
            footer={
              <FooterWrapper>
                {modalActions.map((modalAction, key) => (
                  <div>
                    {this.props.content.forceOpenConsent && (
                      <div
                        style={{ display: 'flex', alignContent: 'center', marginBottom: '10px' }}
                      >
                        <span>
                          {draftToString(
                            this.props.content.scorllToBottomToEnable,
                            this.props.state,
                          )}
                        </span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <CmsButton
                        key={key}
                        disabled={
                          this.props.content.forceOpenConsent && this.activeConsent
                            ? !this.consentScrolledToBottom[this.activeConsent]
                            : false
                        }
                        color={this.props.content.submitButtonBackgroundColor}
                        onClick={(ev) => {
                          runInAction(() => (this.isConfirmed = true));
                          modalAction.onClick(ev);
                        }}
                        text={getTranslation(this.props.state.configuration, modalAction.text)}
                      />
                    </div>
                  </div>
                ))}
              </FooterWrapper>
            }
            fullScreen={this.props.state.windowSize.width < LARGE_SCREEN_SIZE}
            width={this.modalWidth}
            content={
              <PopupContentContainer dangerouslySetInnerHTML={{ __html: this.popupContent }} />
            }
            onClose={this.closePopup}
          />
        )}
        {this.formLinkedData.error &&
        this.formLinkedData.error.errorCode === LinkedDataErrorCodes.FetchDataError ? (
          <ErrorWrapper>
            {draftToString(this.props.content.errorFetchingDataMessage, this.props.state)}
          </ErrorWrapper>
        ) : undefined}
        {this.getFormView(this.formLinkedData.data)}
      </>
    );
  }
}
